import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { ArrowRight } from "lucide-react";
import {
  PageWrapper,
  Main,
  NotFoundSection,
  Content,
  Eyebrow,
  Title,
  Description,
  Actions,
  Button,
  TextLink,
  ArtContainer,
  NumberDisplay,
  ArtSVG,
  QuickLinks,
  QuickLink,
  QuickLinkLabel,
  QuickLinkTitle,
  QuickLinkDesc,
} from "./not-found.styles";

export const NotFound = () => {
  const pathRef = useRef<SVGPathElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const startCircleRef = useRef<SVGCircleElement>(null);

  useEffect(() => {
    // Generate the looping parametric curve
    const pts: [number, number][] = [];
    const loops = 3;
    const a = 13;
    const b = 34;

    for (let k = 0; k <= 600; k++) {
      const t = (k / 600) * loops * 2 * Math.PI;
      pts.push([34 + a * t - b * Math.sin(t), 400 - b * Math.cos(t) - t * 1.2]);
    }

    // Build SVG path
    let d = "M" + pts.map((p) => p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" L");
    const last = pts[pts.length - 1];
    d += ` C${last[0] + 70},${last[1]} 380,456 452,456`;

    // Set path and calculate length for animation
    if (pathRef.current) {
      pathRef.current.setAttribute("d", d);
      const len = Math.ceil(pathRef.current.getTotalLength());
      pathRef.current.style.setProperty("--len", String(len));
    }

    // Position start circle
    if (startCircleRef.current) {
      startCircleRef.current.setAttribute("cx", String(pts[0][0]));
      startCircleRef.current.setAttribute("cy", String(pts[0][1]));
    }

    // Handle viewport switch for mobile
    const handleResize = () => {
      if (svgRef.current) {
        const isMobile = window.innerWidth <= 860;
        svgRef.current.setAttribute("viewBox", isMobile ? "0 270 560 240" : "0 0 560 560");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Page not found · Renée Louise</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <PageWrapper>
        <Main>
          <NotFoundSection>
            <Content>
              <Eyebrow>Error 404 · Page not found</Eyebrow>
              <Title>
                This page took a <em>wrong turn.</em>
              </Title>
              <Description>
                The link may be broken, or the page may have moved. Let's get you back on track.
              </Description>
              <Actions>
                <Button href="/">
                  Back to homepage <ArrowRight size={16} color="#D9442B" aria-hidden="true" />
                </Button>
                <TextLink href="/contact">Book a call</TextLink>
              </Actions>
            </Content>

            <ArtContainer aria-hidden="true">
              <NumberDisplay>404</NumberDisplay>
              <ArtSVG ref={svgRef} viewBox="0 0 560 560">
                <path ref={pathRef} className="line draw" d="" />
                <circle ref={startCircleRef} className="start" r="8" />
                <rect className="sq" x="452" y="420" width="72" height="72" />
                <circle className="dot" cx="488" cy="456" r="7" />
              </ArtSVG>
            </ArtContainer>
          </NotFoundSection>

          <QuickLinks aria-label="Popular pages">
            <QuickLink href="/services">
              <QuickLinkLabel>Services</QuickLinkLabel>
              <QuickLinkTitle>
                What I do <ArrowRight size={16} color="#D9442B" aria-hidden="true" />
              </QuickLinkTitle>
              <QuickLinkDesc>MVPs, AI integration, audits and leadership.</QuickLinkDesc>
            </QuickLink>
            <QuickLink href="/the-guide">
              <QuickLinkLabel>The Guide</QuickLinkLabel>
              <QuickLinkTitle>
                Break into tech <ArrowRight size={16} color="#D9442B" aria-hidden="true" />
              </QuickLinkTitle>
              <QuickLinkDesc>The Unhinged Guide and its 22-part toolkit.</QuickLinkDesc>
            </QuickLink>
            <QuickLink href="/about">
              <QuickLinkLabel>About</QuickLinkLabel>
              <QuickLinkTitle>
                Who I am <ArrowRight size={16} color="#D9442B" aria-hidden="true" />
              </QuickLinkTitle>
              <QuickLinkDesc>From maths teacher to technical delivery.</QuickLinkDesc>
            </QuickLink>
          </QuickLinks>
        </Main>
      </PageWrapper>
    </>
  );
};
